import type { TableColumnDefinition } from "./types";

type HorizontalAlignment = "left" | "right" | "center" | undefined;
type VerticalAlignment = "top" | "bottom" | "center" | undefined;
type TextAlign = "left" | "center" | "right";
type VerticalAlign = "top" | "middle" | "bottom";

export function mapAlignmentToTextAlign(value: HorizontalAlignment): TextAlign {
  if (value === "center") return "center";
  if (value === "right") return "right";
  return "left";
}

export function mapAlignmentToVerticalAlign(
  value: VerticalAlignment,
): VerticalAlign {
  if (value === "top") return "top";
  if (value === "bottom") return "bottom";
  return "middle";
}

export interface ColumnTotals {
  totalFlex: number;
  totalFixedPx: number;
}

export function computeColumnTotals(
  columns: TableColumnDefinition[],
): ColumnTotals {
  let totalFlex = 0;
  let totalFixedPx = 0;
  for (const c of columns) {
    if ("flex" in c && typeof c.flex === "number") totalFlex += c.flex;
    if ("width" in c && typeof c.width === "number") totalFixedPx += c.width;
  }
  return { totalFlex, totalFixedPx };
}

/**
 * Translates the per-column `flex` / `width` hints into a real CSS width
 * shared by the header `<th>` and body `<td>` for that column. `flex: N`
 * means "share of the table width across columns" — exactly what callers
 * already expect; nothing changes at the call site.
 *
 * When `minTableWidth` is supplied we emit absolute pixel widths derived
 * from that total, so `table-layout: fixed` honours them precisely. That's
 * the recipe for "sticky column + horizontal scroll on mobile": the table
 * is at least `minTableWidth` wide, columns sit at known pixel widths, and
 * the sticky column has a stable `width` (sticky positioning needs a
 * predictable size).
 *
 * Without `minTableWidth`, fall back to percentage hints sized against the
 * container — backwards compatible with the 41+ existing call sites.
 *
 * `totals` is precomputed once per render by the parent `Table` so this
 * function stays O(1) per cell instead of O(columns).
 */
export function computeColumnWidth(
  column: TableColumnDefinition,
  totals: ColumnTotals,
  minTableWidth?: number,
): string {
  if ("width" in column && typeof column.width === "number") {
    return `${column.width}px`;
  }
  if (!("flex" in column) || typeof column.flex !== "number") {
    return "auto";
  }
  const { totalFlex, totalFixedPx } = totals;
  if (totalFlex === 0) return "auto";
  const fraction = column.flex / totalFlex;
  if (typeof minTableWidth === "number") {
    const flexBudgetPx = minTableWidth - totalFixedPx;
    if (flexBudgetPx > 0) {
      return `${flexBudgetPx * fraction}px`;
    }
    // Over-constrained (fixed-px columns already exceed minTableWidth):
    // fall through to the percentage path rather than collapse to 0.
  }
  if (totalFixedPx === 0) {
    return `${fraction * 100}%`;
  }
  return `calc((100% - ${totalFixedPx}px) * ${fraction})`;
}
